/**
 * Recently Viewed — lazy panel on scroll + local visitor ID + /products/{handle}?view=card
 */

(function () {
  'use strict';

  const CONFIG = {
    maxStored: 20,
    sentinelId: 'recently-viewed-sentinel',
    mountId: 'recently-viewed-mount',
    containerId: 'recently-viewed-section',
    visitorIdKey: 'rv_visitor_id',
    fallbackIdKey: 'rv_fallback_id',
    legacyHandlesKey: 'rv_handles',
    ioRootMargin: '0px 0px 400px 0px',
  };

  let activeStorageKey = null;
  let loadStarted = false;
  let sentinelWatchCleanup = null;
  const memoryStore = new Map();

  function readStore(key) {
    for (const store of [localStorage, sessionStorage]) {
      try {
        const value = store.getItem(key);
        if (value != null) return value;
      } catch {
        /* Safari private mode / ITP */
      }
    }

    return memoryStore.get(key) ?? null;
  }

  function writeStore(key, value) {
    memoryStore.set(key, value);

    for (const store of [localStorage, sessionStorage]) {
      try {
        store.setItem(key, value);
      } catch {
        /* quota / privacy mode */
      }
    }
  }

  function removeStore(key) {
    memoryStore.delete(key);

    for (const store of [localStorage, sessionStorage]) {
      try {
        store.removeItem(key);
      } catch {
        /* ignore */
      }
    }
  }

  const VisitorId = {
    get() {
      const cached =
        readStore(CONFIG.visitorIdKey) || readStore(CONFIG.fallbackIdKey);

      if (cached) {
        writeStore(CONFIG.visitorIdKey, cached);
        return cached;
      }

      const id =
        typeof crypto?.randomUUID === 'function'
          ? crypto.randomUUID()
          : `fb_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

      writeStore(CONFIG.visitorIdKey, id);
      return id;
    },

    handlesKey(visitorId) {
      return `rv_handles_${visitorId}`;
    },

    migrateLegacyHandles(visitorId) {
      const newKey = this.handlesKey(visitorId);
      if (readStore(newKey)) return;

      const legacy = readStore(CONFIG.legacyHandlesKey);
      if (!legacy) return;

      writeStore(newKey, legacy);
      removeStore(CONFIG.legacyHandlesKey);
    },
  };

  const Storage = {
    get() {
      if (!activeStorageKey) return [];
      try {
        return JSON.parse(readStore(activeStorageKey) || '[]');
      } catch {
        return [];
      }
    },

    save(handle) {
      if (!handle || !activeStorageKey) return;
      let handles = this.get().filter((h) => h !== handle);
      handles.unshift(handle);
      handles = handles.slice(0, CONFIG.maxStored);
      writeStore(activeStorageKey, JSON.stringify(handles));
    },

    getExcluding(currentHandle) {
      return this.get().filter((h) => h && h !== currentHandle);
    },
  };

  const CardFetcher = {
    async fetchCard(handle) {
      const root = window.Shopify?.routes?.root || '/';
      try {
        const res = await fetch(
          `${root}products/${encodeURIComponent(handle)}?view=card`,
          { headers: { Accept: 'text/html' } },
        );
        if (!res.ok) return null;
        const html = (await res.text()).trim();
        return html || null;
      } catch {
        return null;
      }
    },

    async fetchByHandles(handles, maxItems) {
      const slice = handles.slice(0, maxItems);
      const results = await Promise.all(slice.map((h) => this.fetchCard(h)));
      return results.filter(Boolean);
    },
  };

  function loadPageConfig() {
    const node = document.getElementById('recently-viewed-config');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.warn('[RecentlyViewed] Config parse failed:', err);
      return null;
    }
  }

  function peekStoredHandles(currentHandle = '') {
    try {
      const visitorId =
        readStore(CONFIG.visitorIdKey) || readStore(CONFIG.fallbackIdKey);

      if (visitorId) {
        return JSON.parse(readStore(VisitorId.handlesKey(visitorId)) || '[]').filter(
          (handle) => handle && handle !== currentHandle,
        );
      }

      const legacy = readStore(CONFIG.legacyHandlesKey);
      if (!legacy) return [];

      return JSON.parse(legacy).filter((handle) => handle && handle !== currentHandle);
    } catch {
      return [];
    }
  }

  function getSentinel() {
    return document.getElementById(CONFIG.sentinelId);
  }

  function getSkeleton() {
    return document.getElementById('recently-viewed-skeleton');
  }

  function reserveSentinel() {
    const sentinel = getSentinel();
    if (sentinel) {
      sentinel.classList.add('recently-viewed-sentinel--reserved');
      sentinel.classList.remove('recently-viewed-sentinel--empty');
    }
  }

  function showSkeleton() {
    const skeleton = getSkeleton();
    if (!skeleton) return;

    skeleton.hidden = false;
    skeleton.setAttribute('aria-hidden', 'false');
    reserveSentinel();
  }

  function hideSkeleton() {
    const skeleton = getSkeleton();
    if (!skeleton) return;

    skeleton.hidden = true;
    skeleton.setAttribute('aria-hidden', 'true');
  }

  function hideSentinel() {
    const sentinel = getSentinel();
    if (sentinel) {
      sentinel.classList.add('recently-viewed-sentinel--empty');
      sentinel.classList.remove('recently-viewed-sentinel--reserved');
    }

    hideSkeleton();
  }

  function prepareSentinel(pageConfig) {
    const handles = peekStoredHandles(pageConfig?.currentHandle || '');
    if (!handles.length) {
      hideSentinel();
      return false;
    }

    showSkeleton();
    return true;
  }

  function refreshSlider(container) {
    const tryInit = (attempts = 0) => {
      const slideshow = container.querySelector('slideshow-section');
      if (!slideshow) return;

      if (typeof slideshow.swiperInitilize !== 'function') {
        if (attempts < 60) {
          requestAnimationFrame(() => tryInit(attempts + 1));
        }
        return;
      }

      if (slideshow.swiper && typeof slideshow.swiperDestroy === 'function') {
        slideshow.swiperDestroy();
      }

      slideshow.swiperInitilize();
    };

    tryInit();
  }

  function buildSlides(cardsHtml) {
    return cardsHtml
      .map(
        (cardHtml, index) => `
        <div data-swiper-slide>
          <div class="wt-slider__card-wrapper" style="--animation-order: ${index + 1};">
            ${cardHtml}
          </div>
        </div>`,
      )
      .join('');
  }

  function applyPageConfig(container, pageConfig) {
    if (!container || !pageConfig) return;

    const { sectionId, currentHandle, settings } = pageConfig;

    if (sectionId) {
      container.dataset.sectionId = sectionId;
    }

    if (currentHandle) {
      container.dataset.currentHandle = currentHandle;
    }

    if (settings?.products_to_show) {
      container.dataset.maxItems = String(settings.products_to_show);
    }

    if (settings && 'show_secondary_image_on_hover' in settings) {
      container.dataset.showSecondaryImage = String(settings.show_secondary_image_on_hover);
    }

    if (settings) {
      container.classList.toggle(
        'wt-featured-collection--disable-on-mobile',
        Boolean(settings.disable_on_mobile),
      );

      const slideshow = container.querySelector('slideshow-section');
      const swiperEl = container.querySelector('[data-swiper]');
      const scrollbar = container.querySelector('.wt-slider__scrollbar--featured');

      slideshow?.classList.toggle('wt-slider--full', Boolean(settings.make_products_full_width));
      swiperEl?.classList.toggle('wt-slider__container--full', Boolean(settings.make_products_full_width));
      scrollbar?.classList.toggle('wt-slider__scrollbar--full', Boolean(settings.make_products_full_width));

      const headline = container.querySelector('.headline');
      if (settings.heading) {
        const title = container.querySelector('.headline__title');
        if (title) {
          title.textContent = settings.heading;
        }
        headline?.classList.remove('hidden');
      } else {
        headline?.remove();
      }
    }

    const configNode = container.querySelector('[data-swiper-configuration]');
    if (configNode && settings) {
      try {
        const swiperConfig = JSON.parse(configNode.textContent);
        const mobileCols = settings.number_of_column_mobile;
        const desktopCols = settings.number_of_column_desktop;

        if (swiperConfig.breakpoints) {
          if (swiperConfig.breakpoints['320'] && mobileCols) {
            swiperConfig.breakpoints['320'].slidesPerView = mobileCols;
            swiperConfig.breakpoints['320'].slidesPerGroup = Math.floor(mobileCols);
          }
          if (swiperConfig.breakpoints['900'] && desktopCols) {
            swiperConfig.breakpoints['900'].slidesPerView = desktopCols;
            swiperConfig.breakpoints['900'].slidesPerGroup = desktopCols;
          }
        }

        configNode.textContent = JSON.stringify(swiperConfig);
      } catch {
        /* keep fetched defaults */
      }
    }
  }

  function injectPanel(mount, html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const sectionRoot = doc.querySelector('.shopify-section') || doc.body;

    mount.innerHTML = sectionRoot.innerHTML;
  }

  async function fetchPanel(url) {
    const response = await fetch(url, { headers: { Accept: 'text/html' } });
    if (!response.ok) {
      throw new Error('Unable to load recently viewed panel.');
    }
    return response.text();
  }

  async function loadCards(container, pageConfig) {
    const currentHandle = container.dataset.currentHandle || pageConfig?.currentHandle || '';
    const maxItems = parseInt(container.dataset.maxItems, 10) || pageConfig?.settings?.products_to_show || 6;

    if (currentHandle) {
      Storage.save(currentHandle);
    }

    const handles = Storage.getExcluding(currentHandle);
    if (!handles.length) {
      return false;
    }

    const swiperContainer = container.querySelector('[data-swiper-container]');
    if (!swiperContainer) {
      return false;
    }

    const cardsHtml = await CardFetcher.fetchByHandles(handles, maxItems);
    if (!cardsHtml.length) {
      return false;
    }

    swiperContainer.innerHTML = buildSlides(cardsHtml);
    container.querySelector('[data-swiper]')?.classList.remove('loading');
    refreshSlider(container);
    return true;
  }

  function bootstrapStorage(pageConfig) {
    const visitorId = VisitorId.get();
    VisitorId.migrateLegacyHandles(visitorId);
    activeStorageKey = VisitorId.handlesKey(visitorId);

    const currentHandle = pageConfig?.currentHandle || '';
    if (currentHandle) {
      Storage.save(currentHandle);
    }
  }

  function isPanelLoaded() {
    const container = document.getElementById(CONFIG.containerId);
    if (!container) return false;

    const slides = container.querySelectorAll('[data-swiper-slide]');
    if (!slides.length) return false;

    return !slides[0].classList.contains('recently-viewed-skeleton-slide');
  }

  function finishLoadFailure() {
    hideSentinel();
    loadStarted = false;
  }

  async function loadRecentlyViewed(mount, pageConfig) {
    if (loadStarted) return;
    loadStarted = true;

    const url = mount.dataset.url;
    if (!url) {
      loadStarted = false;
      return;
    }

    try {
      bootstrapStorage(pageConfig);

      const currentHandle = pageConfig?.currentHandle || '';
      const handles = Storage.getExcluding(currentHandle);
      if (!handles.length) {
        finishLoadFailure();
        return;
      }

      const panelHtml = await fetchPanel(url);
      injectPanel(mount, panelHtml);

      const container = document.getElementById(CONFIG.containerId);
      if (!container) {
        finishLoadFailure();
        return;
      }

      applyPageConfig(container, pageConfig);
      container.dataset.visitorId = VisitorId.get();

      const hasCards = await loadCards(container, pageConfig);
      if (!hasCards) {
        finishLoadFailure();
        return;
      }

      mount.dataset.recentlyViewedLoaded = 'true';
      hideSkeleton();
      reserveSentinel();
    } catch (err) {
      console.warn('[RecentlyViewed] Failed to load:', err);
      finishLoadFailure();
    }
  }

  function sentinelNearViewport(sentinel) {
    const rect = sentinel.getBoundingClientRect();
    return rect.top <= window.innerHeight + 400;
  }

  function observeSentinel(sentinel, pageConfig) {
    const mount = document.getElementById(CONFIG.mountId);
    if (!mount) return;

    sentinelWatchCleanup?.();

    let triggered = false;
    const cleanups = [];

    const cleanupWatchers = () => {
      cleanups.forEach((fn) => fn());
      cleanups.length = 0;
    };

    const triggerLoad = () => {
      if (triggered) return;
      triggered = true;
      cleanupWatchers();
      sentinelWatchCleanup = null;
      loadRecentlyViewed(mount, pageConfig);
    };

    sentinelWatchCleanup = cleanupWatchers;

    if (sentinelNearViewport(sentinel)) {
      triggerLoad();
      return;
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          observer.disconnect();
          triggerLoad();
        },
        { rootMargin: CONFIG.ioRootMargin, threshold: 0 },
      );
      observer.observe(sentinel);
      cleanups.push(() => observer.disconnect());
    }

    const onScroll = () => {
      if (!sentinelNearViewport(sentinel)) return;
      triggerLoad();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener('scroll', onScroll));
    cleanups.push(() => document.removeEventListener('scroll', onScroll));
  }

  function init() {
    const sentinel = getSentinel();
    if (!sentinel) return;

    const pageConfig = loadPageConfig();
    bootstrapStorage(pageConfig);

    if (isPanelLoaded()) {
      hideSkeleton();
      reserveSentinel();

      const container = document.getElementById(CONFIG.containerId);
      if (container) {
        refreshSlider(container);
      }
      return;
    }

    const shouldReserve = prepareSentinel(pageConfig);
    if (!shouldReserve) return;

    observeSentinel(sentinel, pageConfig);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    sentinelWatchCleanup?.();
    sentinelWatchCleanup = null;
    loadStarted = false;
    init();
  });
})();
